import { useState, type CSSProperties } from 'react';

import { Badge } from '@commenda/pathfinder';
import { CountryFlag } from '@commenda/pathfinder/flags/dynamic';

import { StructureCanvasHelper as C } from '../canvas';
import type { ChartEntity } from '../model';

interface EntityCardProps {
  entity: Readonly<ChartEntity>;
  typeLabel: string | null;
  nameLang: string | undefined;
  isFocused: boolean;
}

const cardStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: C.CARD_GAP,
  boxSizing: 'border-box',
  width: C.NODE_SIZE.width,
  height: C.NODE_SIZE.height,
  paddingLeft: C.CARD_PADDING_LEFT,
  paddingRight: C.CARD_PADDING_RIGHT,
  borderRadius: 'var(--pf-radius-full)',
  border: '0.5px solid var(--pf-color-soft-pearl-300)',
  background: 'var(--pf-color-background)',
};

const hoveredCardStyle: CSSProperties = {
  background: 'var(--pf-color-soft-pearl-200)',
};

// Entities outside the portfolio are shown for context only.
const externalCardStyle: CSSProperties = {
  ...cardStyle,
  borderStyle: 'dashed',
  borderColor: 'var(--pf-color-soft-pearl-500)',
  opacity: 0.75,
};

// Safari paints outer box shadows in exported images as grey boxes, so the shadow
// sits on its own layer that exports leave out.
const cardShadowStyle: CSSProperties = {
  position: 'absolute',
  inset: '-0.5px',
  borderRadius: 'inherit',
  boxShadow: 'var(--pf-shadow-raised)',
  pointerEvents: 'none',
};

// A reference chip just led here.
const focusRingStyle: CSSProperties = {
  boxShadow: '0 0 0 3px var(--pf-color-blue-300)',
  transition: 'box-shadow 200ms ease-out',
};

const flagStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  borderRadius: 'var(--pf-radius-full)',
};

// An inset ring just outside the flag draws the same hairline as an outer one, and
// exports keep it.
const flagRingStyle: CSSProperties = {
  position: 'absolute',
  inset: '-0.5px',
  borderRadius: 'inherit',
  boxShadow: 'inset 0 0 0 0.5px var(--pf-color-soft-pearl-300)',
  pointerEvents: 'none',
};

const textColumnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  minWidth: 0,
  flex: 1,
};

const nameStyle: CSSProperties = {
  maxWidth: '100%',
  margin: 0,
  font: C.NAME_FONT,
  lineHeight: '16px',
  color: 'var(--pf-color-soft-pearl-1000)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const unnamedStyle: CSSProperties = {
  ...nameStyle,
  color: 'var(--pf-color-soft-pearl-700)',
};

const EntityCard = ({
  entity,
  typeLabel,
  nameLang,
  isFocused,
}: EntityCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...(entity.isExternal ? externalCardStyle : cardStyle),
        ...(isHovered && !entity.isExternal ? hoveredCardStyle : undefined),
        ...(isFocused ? focusRingStyle : undefined),
      }}
    >
      {!entity.isExternal && <span data-capture-skip style={cardShadowStyle} />}
      <span style={flagStyle}>
        <CountryFlag
          code={entity.source.incorporationCountry ?? ''}
          size={C.FLAG_SIZE}
        />
        <span style={flagRingStyle} />
      </span>
      <div style={textColumnStyle}>
        <p
          lang={nameLang}
          style={entity.source.legalName?.trim() ? nameStyle : unnamedStyle}
        >
          {entity.name}
        </p>
        {typeLabel && (
          <Badge
            label={typeLabel}
            className="max-w-full whitespace-nowrap [&>span]:truncate"
          />
        )}
      </div>
    </div>
  );
};

export { EntityCard };
